import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Alert } from '@/components/Alert';
import { CheckBox } from '@/components/CheckBox';
import { FormInput } from '@/components/FormInput';
import { ContactInfoMobile } from '@/components/ContactInfoMobile';
import Spinner from '@/components/UI/Spinner';
import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { COUNTRY_PHONE_PREFIX } from '@/constants/phonePrefixes';
import { RootState } from '@/models/root-state.model';
import { updateConsentsSuccess } from '@/store/actions/consentsActions';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { getUserSuccess } from '@/store/actions/user';
import { BffUserUpdateContactInfoResponse } from '@lucky7ventures/bff-types';
import { BaseModalProps } from '@/features/modal/types';

export const ContactInfoModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();

  const [smsChecked, setSmsChecked] = useState(true);
  const [emailChecked, setEmailChecked] = useState(true);
  const { request, error, loading } = useBffApiRequest<BffUserUpdateContactInfoResponse>();
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.email.required' }))
      .email(intl.formatMessage({ id: 'inputs.email.invalid' })),
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'completeProfile.mobile.error' })),
    }),
  });

  const initialValues = {
    email: '',
    mobile: {
      number: '',
      country: user.countryCode,
      prefix: COUNTRY_PHONE_PREFIX[user.countryCode],
      wholeNumber: COUNTRY_PHONE_PREFIX[user.countryCode],
    },
  };

  const submitHandler = (values: any): void => {
    const contactInfoReq = {
      email: values.email,
      mobileNumber: values.mobile.number,
      mobilePrefix: values.mobile.prefix,
      smsConsented: smsChecked,
      emailConsented: emailChecked,
    };
    request({
      apiMethod: BffApiService.updateContactInfo,
      payload: contactInfoReq,
      successCallback: (data: BffUserUpdateContactInfoResponse) => {
        dispatch(getUserSuccess(data.user));
        dispatch(updateConsentsSuccess(data.consents));
        closeModal();
      },
    });
  };

  return (
    <ModalLayout close={closeModal} className="max-w-md">
      <div id="e2e_contactInfo" className="w-full">
        <h1 className="mb-8 text-2xl">
          {intl.formatMessage({ id: 'updateContactInfo.modal.title' })}
        </h1>
        <div className="content">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={values => submitHandler(values)}
          >
            {({ isValid }) => (
              <Form>
                <FormInput
                  name="email"
                  type="email"
                  label={intl.formatMessage({
                    id: 'inputs.email',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'inputs.email.placeholder',
                  })}
                />
                <ContactInfoMobile
                  label={intl.formatMessage({ id: 'completeProfile.mobile.placeholder' })}
                  placeholder="+358 12 345 67 89"
                  name="mobile"
                />
                <div className="consent flex flex-col justify-between pt-6">
                  <p className="mb-4 text-sm">
                    {intl.formatMessage({ id: 'completeProfile.optin.header' })}
                  </p>
                  <div className="checkboxes flex items-center justify-around">
                    <CheckBox
                      className="mx-2"
                      id="smsOptIn"
                      value="checked"
                      label={intl.formatMessage({ id: 'inputs.toggle.sms' })}
                      initialCheck={smsChecked}
                      handleChange={() => setSmsChecked(prevValue => !prevValue)}
                    />

                    <CheckBox
                      className="mx-2"
                      id="emailOptIn"
                      value="checked"
                      label={intl.formatMessage({ id: 'inputs.toggle.email' })}
                      initialCheck={emailChecked}
                      handleChange={() => setEmailChecked(prevValue => !prevValue)}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!isValid}
                  className="my-4 block h-14 w-full cursor-pointer rounded-full bg-blue-light font-bold text-white shadow-md transition hover:bg-cyan-600 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 md:px-4"
                >
                  {loading && <Spinner width={18} height={18} borderwidth={3} color="white" />}
                  {!loading && <>{intl.formatMessage({ id: 'misc.update' })}</>}
                </button>
              </Form>
            )}
          </Formik>
          {error && (
            <Alert
              text={generateBffErrorMessageWithCode(
                intl.formatMessage({ id: 'error.support' }),
                error.code,
              )}
            />
          )}
        </div>
      </div>
    </ModalLayout>
  );
};
